import { consume } from '@lit/context'
import { SchmancyElement } from '@schmancy/mixins/index'
import { css, html } from 'lit'
import { customElement, property, query, queryAssignedElements, state } from 'lit/decorators.js'
import { Observable, takeUntil } from 'rxjs'
import { SchmancyEvents } from '@schmancy/types'
import { OVERLAY_DISMISS_EVENT } from './events'
import {
	SchmancyContentDrawerID,
	SchmancyContentDrawerMaxHeight,
	SchmancyContentDrawerMinWidth,
	SchmancyContentDrawerSheetMode,
	SchmancyContentDrawerSheetState,
	TSchmancyContentDrawerSheetMode,
	TSchmancyContentDrawerSheetState,
} from './context'
import '@schmancy/router/area.component'

@customElement('schmancy-content-drawer-sheet')
export class SchmancyContentDrawerSheet extends SchmancyElement {
	static styles = [
		css`
			:host {
				overflow: scroll;
			}
		`,
	]

	@property({ type: Number })
	minWidth

	@consume({ context: SchmancyContentDrawerSheetMode, subscribe: true })
	@state()
	mode: TSchmancyContentDrawerSheetMode

	@consume({ context: SchmancyContentDrawerSheetState, subscribe: true })
	@state()
	state: TSchmancyContentDrawerSheetState

	@consume({ context: SchmancyContentDrawerID })
	schmancyContentDrawerID

	@query('#sheet') sheet!: HTMLElement
	@queryAssignedElements({ flatten: true, slot: undefined }) defaultSlot!: HTMLElement[]

	@consume({ context: SchmancyContentDrawerMinWidth, subscribe: true })
	drawerMinWidth: typeof SchmancyContentDrawerMinWidth.__context__

	@consume({ context: SchmancyContentDrawerMaxHeight, subscribe: true })
	@state()
	maxHeight

	connectedCallback(): void {
		super.connectedCallback()
		if (this.minWidth) {
			this.drawerMinWidth.sheet = this.minWidth
		} else {
			this.minWidth = this.drawerMinWidth.sheet
		}
	}

	updated(changedProperties: Map<string, unknown>) {
		super.updated(changedProperties)
		if (changedProperties.has('minWidth') && this.minWidth) {
			this.drawerMinWidth.sheet = this.minWidth
			this.dispatchEvent(new CustomEvent(SchmancyEvents.ContentDrawerResize, { bubbles: true, composed: true }))
		} else if (changedProperties.has('state') || changedProperties.has('mode')) {
			if (this.mode === 'overlay') {
				if (this.state === 'close') {
					this.closeAll()
				} else if (this.state === 'open') {
				}
			} else if (this.mode === 'push') {
				this.requestOverlayDismiss()
				if (this.state === 'close') {
					this.closeAll()
				} else if (this.state === 'open') {
					this.open()
				}
			}
		}
	}

	open() {
		if (this.mode === 'overlay') {
			this.sheet.style.position = 'fixed'
		} else {
			this.sheet.style.position = 'relative'
		}
		this.sheet.style.display = 'block'

		this.sheet.animate(
			[
				{ opacity: 0, transform: 'translateX(100%)' },
				{ opacity: 1, transform: 'translateX(0%)' },
			],
			{
				duration: 250,
				easing: 'cubic-bezier(0.5, 0.01, 0.25, 1)',
			},
		)
	}

	/** Bubbles to the parent `<schmancy-content-drawer>` to dismiss its modal overlay. */
	private requestOverlayDismiss() {
		this.dispatchEvent(new CustomEvent(OVERLAY_DISMISS_EVENT, { bubbles: true, composed: true }))
	}

	/** Fires the overlay dismiss and the sheet close in parallel. */
	closeAll() {
		this.requestOverlayDismiss()
		this.closeSheet().pipe(takeUntil(this.disconnecting)).subscribe()
	}

	/** Observable return lets callers merge this with other close operations. */
	closeSheet() {
		return new Observable<void>(observer => {
			const animation = this.sheet.animate(
				[
					{ opacity: 1, transform: 'translateX(0%)' },
					{ opacity: 1, transform: 'translateX(100%)' },
				],
				{
					duration: 250,
					easing: 'cubic-bezier(0.5, 0.01, 0.25, 1)',
				},
			)

			animation.onfinish = () => {
				this.sheet.style.display = 'none'
				observer.next()
				observer.complete()
			}
		})
	}

	protected render() {
		const sheetClasses = {
			'block h-full w-full': this.mode === 'push',
			'absolute z-50': this.mode === 'overlay',
			'opacity-1': this.mode === 'overlay' && this.state === 'open',
		}

		const styles = {
			minWidth: `${this.minWidth}px`,
			maxHeight: this.maxHeight,
		}

		return html`
			<section id="sheet" class="${this.classMap(sheetClasses)}" style=${this.styleMap(styles)}>
				<schmancy-area class="h-full w-full" name="${this.schmancyContentDrawerID}">
					<slot name="placeholder"></slot>
				</schmancy-area>
			</section>
		`
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'schmancy-content-drawer-sheet': SchmancyContentDrawerSheet
	}
}
